import { useGlobalState } from '../GlobalState';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Checkbox, Form, Input, Select, Spin } from 'antd';
import CardInput, { cardInputValidator } from './CardInput';
import { LoadingOutlined, LockFilled } from '@ant-design/icons';
import opayoLogo from '../../assets/images/Opayo_Logo_ByE_Small_CMYK.png';
import countryCodes from '../../assets/data/iso3166_country_codes';
import Secure3DChallengeForm from './Secure3DChallengeForm';
import { useFormSubmitCheck } from '../../../../utils/hooks';
import { costString } from '../../../../utils/cost';

const spinIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

function PaymentForm({
  idField = {name: 'booking_public_id', value: 0},
  amount,
  onPaymentSuccess,
  onPaymentFailure,
  enableSaveCard = false,
}) {
  const [paymentFormData, setPaymentFormData] =
    useGlobalState('paymentFormData');
  const [, setPaymentError] = useGlobalState('paymentError');
  const [, setPaymentSuccessRedirect] = useGlobalState('paymentSuccessRedirect');
  const [, setBookingTrialIssued] = useGlobalState('bookingTrialIssued');
  const [submitting, setSubmitting] = useState(false);
  const paymentFormWrapperRef = useRef();
  const [secure3DFormData, setSecure3DFormData] = useState(null);
  const [form] = Form.useForm();
  const [handleFormChange, disableSubmit] = useFormSubmitCheck(form, [
    'card_details',
    'name',
    'address',
    'country',
    'postcode',
  ]);

  const countryOptions = useMemo(() => {
    return (
      countryCodes
        // Move United Kingdom to the start
        .sort(({ Code: x }, { Code: y }) =>
          x === 'GB' ? -1 : y === 'GB' ? 1 : 0
        )
        .map(({ Code, Name }) => ({
          label: Name,
          value: Code,
        }))
    );
  }, []);

  const registerPayment = useCallback(
    (paymentFormData) => {
      const data = {
        [idField.name]: idField.value,
        card_details: {
          cardholder_name: paymentFormData.name,
          card_type: paymentFormData.card_details.cardType,
          card_number: paymentFormData.card_details.cardNumber.replaceAll(
            ' ',
            ''
          ),
          card_expiry_date: paymentFormData.card_details.expiryDate.replaceAll(
            '/',
            ''
          ),
          cv2: paymentFormData.card_details.cvc,
          billing_address: {
            addressLine1: paymentFormData.address,
            postcode: paymentFormData.postcode,
            country: paymentFormData.country,
          },
          save_card: paymentFormData?.saveCard ?? undefined,
        },
        secure_3d_window_details: {
          width: paymentFormWrapperRef?.current?.offsetWidth || 600,
          redirect_domain: window.location.origin,
        },
        browser_details: {
          language: navigator.language,
          color_depth: window.screen.colorDepth,
          screen_height: window.screen.height,
          screen_width: window.screen.width,
          timezone: new Date().getTimezoneOffset(),
        },
      };

      const axios_config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      };

      setSubmitting(true);
      setPaymentError(null);
      setPaymentSuccessRedirect(null);
      setBookingTrialIssued(false);
      // Stored for payment re-attempts in case of error
      setPaymentFormData(paymentFormData);
      axios
        .post('payment/direct/register', data, axios_config)
        .then((res) => {
          if (res.status === 200) {
            const res_data = res.data;
            if (res_data?.secure_3d_details) {
              setSecure3DFormData(res_data.secure_3d_details);
            } else {
              setPaymentSuccessRedirect(res_data?.redirect);
              if (res?.data?.trial_issued !== undefined) {
                setBookingTrialIssued(Boolean(res?.data?.trial_issued))
              }
              onPaymentSuccess();
            }
          } else {
            setPaymentError(res?.data?.detail);
            onPaymentFailure();
          }
        })
        .catch((err) => {
          setPaymentError(err?.response?.data?.detail);
          onPaymentFailure();
        })
        .finally(() => setSubmitting(false));
    },
    [idField.name, idField.value, setPaymentError, setPaymentFormData, onPaymentSuccess, onPaymentFailure]
  );

  const handleFormSubmit = useCallback(
    (data) => {
      registerPayment(data);
    },
    [registerPayment]
  );

  if (secure3DFormData) {
    return (
      <Secure3DChallengeForm
        acs_url={secure3DFormData.acs_url}
        creq={secure3DFormData.creq}
        secure_3d_session_data={secure3DFormData.secure_3d_session_data}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentFailure={onPaymentFailure}
      />
    );
  }

  return (
    <div ref={paymentFormWrapperRef}>
      <Form
        className='payment_form'
        requiredMark={false}
        onFinish={handleFormSubmit}
        labelCol={{ span: 6 }}
        labelAlign='left'
        initialValues={paymentFormData || undefined}
        form={form}
        onFieldsChange={handleFormChange}
      >
        <Form.Item
          label={'Card Information'}
          name={'card_details'}
          validateTrigger={'onSubmit'}
          required
          rules={[
            {
              validator: cardInputValidator,
            },
          ]}
        >
          <CardInput />
        </Form.Item>
        <Form.Item
          label={'Name on Card'}
          name={'name'}
          rules={[
            {
              required: true,
              message: 'Please input the name on your card',
            },
          ]}
        >
          <Input className={'br5'} />
        </Form.Item>
        <Form.Item
          label={'Street Address'}
          name={'address'}
          rules={[
            {
              required: true,
              message: 'Please input your billing address',
            },
          ]}
        >
          <Input className={'br5'} />
        </Form.Item>
        <Form.Item
          label={'Country'}
          name={'country'}
          rules={[
            {
              required: true,
              message: 'Please select your country',
            },
          ]}
        >
          <Select
            className={'br5'}
            popupClassName={'payment_form__selector'}
            options={countryOptions}
            virtual={
              false
            } /* Resolves https://github.com/ant-design/ant-design/issues/26708 */
          />
        </Form.Item>
        <Form.Item
          label={'Post Code'}
          name={'postcode'}
          validateTrigger={'onSubmit'}
          rules={[
            {
              required: true,
              message: 'Please input your postcode',
            },
          ]}
        >
          <Input className={'br5'} />
        </Form.Item>
        {enableSaveCard && (
          <div className={'txt-center gRegular'}>
            <Form.Item
              name={'saveCard'}
              className={'mg-bottom-5'}
              valuePropName='checked'
            >
              <Checkbox>
                {' '}
                Save my card for future transactions (one card can be saved at a
                time)
              </Checkbox>
            </Form.Item>
          </div>
        )}
        <Form.Item
          extra={
            <div className={'txt-center mg-top-5 gMedium black'}>
              <LockFilled /> Secure Checkout Powered By{' '}
              <img
                src={opayoLogo}
                alt={'Opayo'}
                style={{ maxHeight: '2em' }}
                className={'mg-left-5'}
              />
            </div>
          }
        >
          {submitting ? (
            <div className={' txt-center w-full mg-top-20 mg-bottom-20'}>
              <Spin indicator={spinIcon} />
            </div>
          ) : (
            <Button
              id='payment_button'
              className={
                'payment_form__submit sb-blue-bg white w-full gSemiBold fs-20 pd-10 h-full br5 mg-top-10 '
              }
              disabled={disableSubmit}
              htmlType='submit'
            >
              Pay {amount ? costString(amount) : '£unknown'}
            </Button>
          )}
        </Form.Item>
      </Form>
    </div>
  );
}

export default PaymentForm;
